import { z } from 'zod'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'
import { LookupHeadersModelSchema } from './lookup_headers_model.ts'
import { CountriesModelSchema } from './countries_model.ts'
import { CurrenciesModelSchema } from './currencies_model.ts'
import { PipelineStagesModelSchema } from './pipeline_stages_model.ts'
import { PipelinesModelSchema } from './pipelines_model.ts'
import { PipelineConfigModelSchema } from './pipeline_config_model.ts'
import { FundsModelSchema } from './funds_model.ts'
import { FundStatusModelSchema } from './fund_status_model.ts'
import { FundGroupConfigModelSchema } from './fund_group_config_model.ts'
import { PipelineDealTypeExclusionModelSchema } from './pipeline_deal_type_exclusion_model.ts'
import { PipelineOutcomeModelSchema } from './pipeline_outcome_model.ts'
import { PipelineStatusConfigModelSchema } from './pipeline_status_config_model.ts'
import { InvestorParentConfigModelSchema } from './investor_parent_config_model.ts'
import { AccountOwnerRoleModelSchema } from './account_owner_role_model.ts'
import { RelationshipFactTypeModelSchema } from './relationship_fact_type_model.ts'
import { AgentDraftKindModelSchema } from './agent_draft_kind_model.ts'
import { AccountRoleChoicesModelSchema } from './account_role_choices_model.ts'
import { InvestorClassificationModelSchema } from './investor_classification_model.ts'
import { SbicInvestorTypeModelSchema } from './sbic_investor_type_model.ts'
import { ShareRegisterModelSchema } from './share_register_model.ts'
import { RelationshipCategoriesModelSchema } from './relationship_categories_model.ts'
import { RelationshipTypesModelSchema } from './relationship_types_model.ts'
import { ReportingColumnTypeModelSchema } from './reporting_column_type_model.ts'
import { ModulesModelSchema } from './modules_model.ts'
import { ReportingConfigModelSchema } from './reporting_config_model.ts'
import { ReportingModulesModelSchema } from './reporting_modules_model.ts'
import { ReportingColumnDefaultsModelSchema } from './reporting_column_defaults_model.ts'
import { ReportingColumnsModelSchema } from './reporting_columns_model.ts'
import { ReportingDefaultSortModelSchema } from './reporting_default_sort_model.ts'
import { UserEntrySchema } from './user_entry.ts'
import { MenuSectionModelSchema } from './menu_section_model.ts'
import { MenuCategoryModelSchema } from './menu_category_model.ts'
import { MenuItemModelSchema } from './menu_item_model.ts'
import { UserPreferenceReportModelSchema } from './user_preference_report_model.ts'
import { UserPreferenceAppModelSchema } from './user_preference_app_model.ts'
import { CommsTopicModelSchema } from './comms_topic_model.ts'
import { CommsLpStatusModelSchema } from './comms_lp_status_model.ts'
import { CommsLpStatusRuleModelSchema } from './comms_lp_status_rule_model.ts'
import { CommsTopicRoleModelSchema } from './comms_topic_role_model.ts'
import { EmailTemplateModelSchema } from './email_template_model.ts'
import { DocumentReportModelSchema } from './document_report_model.ts'
import { EditorConfigSchema } from './editor_config_model.ts'
import { AgentAccessConfigSchema } from './agent_access_model.ts'
import { CapabilityGrantModelSchema } from './capability_grant_model.ts'
import {
  AssetTransactionTypeModelSchema,
  AssetTransactionScopeModelSchema,
  AssetValuationTypeModelSchema,
  AtAccountingTreatmentModelSchema,
  SecurityTypeModelSchema,
  ValuationCapTypeModelSchema,
  InterestRateTypeModelSchema,
  InterestPaymentTypeModelSchema
} from './asset_transaction_lookups_model.ts'
import { NumberFormatModelSchema } from './number_format_model.ts'
import {
  TimeUnitModelSchema,
  ReferenceDateTypeModelSchema,
  GpCommitmentBasisModelSchema,
  FeeBasisModelSchema,
  LateCloserPolicyModelSchema,
  FeeBreakTreatmentModelSchema,
  FeePeriodSwitchTimingModelSchema,
  WaivedFeeSettlementModelSchema,
  CatchupKindModelSchema,
  FeeReductionSchemeModelSchema,
  CompoundingFrequencyModelSchema,
  DayCountConventionModelSchema,
  FundTransactionTypeModelSchema,
  AccItemModelSchema
} from './fund_accounting_lookups_model.ts'
import {
  WaterfallBasisModelSchema,
  WaterfallRateSourceModelSchema,
  WaterfallLookupModelSchema,
  WaterfallTemplateModelSchema,
  WaterfallHistoryCheckModelSchema,
  DistributionStatusModelSchema,
  DistributionKindModelSchema
} from './waterfall_lookups_model.ts'

export const GlobalConfigRecordSchema = z
  .object({
    lookup_headers: z.array(LookupHeadersModelSchema),
    countries: z.array(CountriesModelSchema),
    currencies: z.array(CurrenciesModelSchema),
    pipeline_stages: z.array(PipelineStagesModelSchema),
    pipelines: z.array(PipelinesModelSchema),
    pipeline_configs: z.array(PipelineConfigModelSchema),
    // Deal types barred from a pipeline kind. Read by the New Deal drawer to
    // hide the Pipeline Stage field. Defaulted for pre-feature backends.
    pipeline_deal_type_exclusions: z
      .array(PipelineDealTypeExclusionModelSchema)
      .optional()
      .default([]),
    // What a pipeline process ends AS, and whether it has ended yet
    // (20261112120000). Both are top-level keys rather than lookup groups: a
    // lookup detail carries neither a tag colour nor is_terminal/outcome_id,
    // and those are what every consumer actually reads. .optional().default([])
    // is load-bearing the same way fund_statuses' is — it lets the frontend run
    // against a backend that predates the outcomes work.
    pipeline_outcomes: z.array(PipelineOutcomeModelSchema).optional().default([]),
    pipeline_statuses: z
      .array(PipelineStatusConfigModelSchema)
      .optional()
      .default([]),
    funds: z.array(FundsModelSchema),
    // .optional().default([]) is load-bearing: it lets the frontend run against
    // a backend that predates the fund-status work, and combined with the
    // fail-open gate that degrades to "every fund active" rather than "every
    // picker empty".
    fund_statuses: z.array(FundStatusModelSchema).optional().default([]),
    // The fundraising board's default fund. NULL is valid — the picker then
    // opens empty. Replaces a hardcoded PROD uuid in PipelineFundSelect.
    fundraising_default_fund_id: z.string().nullish(),
    // Consolidated funds (fund groups) — separate from `funds` so groups
    // never leak into fund selectors. Defaulted for pre-feature backends.
    fund_groups: z.array(FundGroupConfigModelSchema).optional().default([]),
    account_role_choices: z.array(AccountRoleChoicesModelSchema),
    investor_classifications: z.array(InvestorClassificationModelSchema),
    // SBIC investor types (cfg.sbic_investor_types). Defaulted for pre-feature backends.
    sbic_investor_types: z.array(SbicInvestorTypeModelSchema).optional().default([]),
    // Fee break treatments (cfg.fee_break_treatments, spec A3.9). Defaulted for pre-feature backends.
    fee_break_treatments: z.array(FeeBreakTreatmentModelSchema).optional().default([]),
    // Fee period switch timings (cfg.fee_period_switch_timings). Defaulted for pre-feature backends.
    fee_period_switch_timings: z.array(FeePeriodSwitchTimingModelSchema).optional().default([]),
    // Waived fee settlements (cfg.waived_fee_settlements, spec A3.9 addendum part 1). Defaulted for pre-feature backends.
    waived_fee_settlements: z.array(WaivedFeeSettlementModelSchema).optional().default([]),
    // Distribution waterfall configuration (docs/WATERFALL_CARRY_DESIGN.md phase 1): the lookups
    // and templates behind the Terms tab Waterfall section. Defaulted for pre-feature backends.
    waterfall_bases: z.array(WaterfallBasisModelSchema).optional().default([]),
    waterfall_rate_sources: z.array(WaterfallRateSourceModelSchema).optional().default([]),
    waterfall_tier_types: z.array(WaterfallLookupModelSchema).optional().default([]),
    waterfall_stop_metrics: z.array(WaterfallLookupModelSchema).optional().default([]),
    waterfall_stop_bases: z.array(WaterfallLookupModelSchema).optional().default([]),
    waterfall_recipient_classes: z.array(WaterfallLookupModelSchema).optional().default([]),
    waterfall_scopes: z.array(WaterfallLookupModelSchema).optional().default([]),
    waterfall_templates: z.array(WaterfallTemplateModelSchema).optional().default([]),
    // How a waterfall checks its earlier distributions (phase 6). Defaulted for pre-feature backends.
    waterfall_history_checks: z.array(WaterfallHistoryCheckModelSchema).optional().default([]),
    // The Distribution transaction (phase 3): statuses and kinds behind the distribution form.
    distribution_statuses: z.array(DistributionStatusModelSchema).optional().default([]),
    distribution_kinds: z.array(DistributionKindModelSchema).optional().default([]),
    share_registers: z.array(ShareRegisterModelSchema),
    relationship_categories: z.array(RelationshipCategoriesModelSchema),
    relationship_types: z.array(RelationshipTypesModelSchema),
    comms_topics: z.array(CommsTopicModelSchema).optional().default([]),
    comms_lp_statuses: z.array(CommsLpStatusModelSchema).optional().default([]),
    comms_lp_status_rules: z.array(CommsLpStatusRuleModelSchema).optional().default([]),
    comms_topic_roles: z.array(CommsTopicRoleModelSchema).optional().default([]),
    menu_sections: z.array(MenuSectionModelSchema).optional().default([]),
    menu_categories: z.array(MenuCategoryModelSchema).optional().default([]),
    menu_items: z.array(MenuItemModelSchema).optional().default([]),
    user_preferences_reports: z.array(UserPreferenceReportModelSchema).optional().default([]),
    // Single row, or null when the user has never stored a global preference.
    // .nullish() (not .optional()): the backend validator runs with
    // add_nullables=True, so a missing key is materialised as an explicit null.
    user_preferences_app: UserPreferenceAppModelSchema.nullish(),
    reporting_column_types: z.array(ReportingColumnTypeModelSchema).optional().default([]),
    modules: z.array(ModulesModelSchema).optional().default([]),
    reporting_configs: z.array(ReportingConfigModelSchema).optional().default([]),
    reporting_modules: z.array(ReportingModulesModelSchema).optional().default([]),
    reporting_column_defaults: z.array(ReportingColumnDefaultsModelSchema).optional().default([]),
    reporting_columns: z.array(ReportingColumnsModelSchema).optional().default([]),
    reporting_default_sorts: z.array(ReportingDefaultSortModelSchema).optional().default([]),
    email_templates: z.array(EmailTemplateModelSchema).optional().default([]),
    time_units: z.array(TimeUnitModelSchema).optional().default([]),
    reference_date_types: z.array(ReferenceDateTypeModelSchema).optional().default([]),
    gp_commitment_bases: z.array(GpCommitmentBasisModelSchema).optional().default([]),
    fee_bases: z.array(FeeBasisModelSchema).optional().default([]),
    late_closer_policies: z.array(LateCloserPolicyModelSchema).optional().default([]),
    catchup_kinds: z.array(CatchupKindModelSchema).optional().default([]),
    fee_reduction_schemes: z.array(FeeReductionSchemeModelSchema).optional().default([]),
    compounding_frequencies: z.array(CompoundingFrequencyModelSchema).optional().default([]),
    day_count_conventions: z.array(DayCountConventionModelSchema).optional().default([]),
    fund_transaction_types: z.array(FundTransactionTypeModelSchema).optional().default([]),
    acc_items: z.array(AccItemModelSchema).optional().default([]),
    asset_transaction_types: z.array(AssetTransactionTypeModelSchema).optional().default([]),
    asset_transaction_scopes: z.array(AssetTransactionScopeModelSchema).optional().default([]),
    asset_valuation_types: z.array(AssetValuationTypeModelSchema).optional().default([]),
    number_formats: z.array(NumberFormatModelSchema).optional().default([]),
    security_types: z.array(SecurityTypeModelSchema).optional().default([]),
    valuation_cap_types: z.array(ValuationCapTypeModelSchema).optional().default([]),
    interest_rate_types: z.array(InterestRateTypeModelSchema).optional().default([]),
    // cfg.interest_payment_types (Security form, Interest terms). Defaulted
    // for pre-feature backends.
    interest_payment_types: z.array(InterestPaymentTypeModelSchema).optional().default([]),
    at_accounting_treatments: z.array(AtAccountingTreatmentModelSchema).optional().default([]),
    document_reports: z.array(DocumentReportModelSchema).optional().default([]),
    // Investor parents with closed commitments — the Investor Performance
    // Report's parent-picker options. Defaulted for pre-feature backends.
    investor_parents: z.array(InvestorParentConfigModelSchema).optional().default([]),
    // Relationship ownership + knowledge ledger lookups (Phase 0 of the LP
    // agent programme). Defaulted for pre-feature backends.
    account_owner_roles: z.array(AccountOwnerRoleModelSchema).optional().default([]),
    relationship_fact_types: z.array(RelationshipFactTypeModelSchema).optional().default([]),
    agent_draft_kinds: z.array(AgentDraftKindModelSchema).optional().default([]),
    editor_config: EditorConfigSchema.optional(),
    agent_access: AgentAccessConfigSchema.optional(),
    current_user: UserEntrySchema.nullish(),
    // Is the CALLING user an application administrator (cfg.app_admins)?
    // Gates admin-only surfaces in the UI — currently the Connected Agents
    // "Agent products" tab. A display hint only: view__agent_products and
    // set_agent_product_enabled are guarded by public.is_app_admin() in the
    // database, so hiding the tab withholds a control the backend refuses
    // anyway. Defaulted false so a pre-feature backend degrades to hiding the
    // tab (fail-closed) rather than failing Zod validation on the whole
    // global-configs payload.
    is_app_admin: z.boolean().optional().default(false),

    // Per-user menu visibility: does this user have any non-trashed agent
    // grant? Paired with menu_items.visibility_rule = 'has_connected_agent'.
    // Same per-request, never-cached posture as is_app_admin above; defaults
    // false so a payload from a backend that predates the flag hides the item
    // rather than showing one that leads nowhere.
    has_connected_agent: z.boolean().optional().default(false),

    // The calling user's rights (docs/USER_RIGHTS_DESIGN.md), read through
    // core/hooks/useCapability. Same per-request, never-cached posture as
    // is_app_admin; defaulted empty so a pre-feature backend leaves every
    // capability not allowed on screen (fail-closed) rather than failing Zod
    // validation on the whole payload.
    capabilities: z.array(CapabilityGrantModelSchema).optional().default([])
  })
  .strict()

const GlobalConfigRecordFields = makeFieldKeys(GlobalConfigRecordSchema)

export type GlobalConfigRecord = z.infer<typeof GlobalConfigRecordSchema>

export function validateGlobalConfigRecord(data: unknown): GlobalConfigRecord {
  try {
    return validateWithZodSchema(GlobalConfigRecordSchema, data, 'global config record')
  } catch (error) {
    throw new Error(`Global config record validation failed: ${error}`)
  }
}
